import React, { useEffect, useState } from "react";
import LibraryFormModal from "../components/LibraryFormModal";
import LibraryField, {
  libraryInputClass,
  librarySelectClass,
} from "../components/LibraryField";
import { ChevronDownIcon } from "../../../Icons";
import {
  CARE_PLAN_TEMPLATE_CATEGORIES,
  isCarePlanMetaSection,
} from "./carePlanSections";

const EMPTY = {
  templateText: "",
  category: "",
  displayOrder: 0,
  status: "Active",
};

const CarePlanTemplateFormModal = ({
  isOpen,
  record,
  onClose,
  onSave,
  sectionKey,
  sectionLabel = "Template",
}) => {
  const isEdit = Boolean(record?.id);
  const requireCategory = !isCarePlanMetaSection(sectionKey);
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setErrors({});
    setSaving(false);
    setForm(
      record
        ? {
            templateText: record.templateText || "",
            category: record.category || "",
            displayOrder:
              record.displayOrder == null ? 0 : Number(record.displayOrder),
            status: record.status || "Active",
          }
        : {
            ...EMPTY,
            category: requireCategory ? CARE_PLAN_TEMPLATE_CATEGORIES[0] : "",
          },
    );
  }, [isOpen, record, requireCategory]);

  const setField = (key, value) => {
    setForm((f) => ({ ...f, [key]: value }));
    if (errors[key]) setErrors((e) => ({ ...e, [key]: undefined }));
  };

  const handleSubmit = async () => {
    const nextErrors = {};
    if (!form.templateText?.trim()) {
      nextErrors.templateText = "Template is required";
    }
    if (requireCategory && !form.category?.trim()) {
      nextErrors.category = "Category is required";
    }
    if (
      requireCategory &&
      form.category &&
      !CARE_PLAN_TEMPLATE_CATEGORIES.includes(form.category)
    ) {
      nextErrors.category = "Select a valid category";
    }
    const order = Number(form.displayOrder);
    if (!Number.isFinite(order) || order < 0) {
      nextErrors.displayOrder = "Display order must be 0 or greater";
    }
    if (Object.keys(nextErrors).length) {
      setErrors(nextErrors);
      return;
    }

    setSaving(true);
    try {
      const payload = {
        templateText: form.templateText.trim(),
        category: requireCategory ? form.category : form.category?.trim() || "",
        displayOrder: Math.trunc(order),
        status: form.status || "Active",
      };
      if (!isEdit) payload.sectionKey = sectionKey;
      await onSave?.(payload);
      onClose?.();
    } catch (err) {
      setErrors({ form: err.message || "Save failed" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <LibraryFormModal
      isOpen={isOpen}
      title={
        isEdit
          ? `Edit ${sectionLabel}`
          : `Create ${sectionLabel}`
      }
      onClose={onClose}
      onSubmit={handleSubmit}
      isSaving={saving}
      submitLabel={isEdit ? "Update" : "Create"}
    >
      {errors.form ? (
        <p className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-md px-3 py-2">
          {errors.form}
        </p>
      ) : null}

      <LibraryField
        id="cpt-template"
        label="Template"
        required
        error={errors.templateText}
      >
        <textarea
          id="cpt-template"
          rows={5}
          value={form.templateText}
          onChange={(e) => setField("templateText", e.target.value)}
          className={`${libraryInputClass(Boolean(errors.templateText))} h-auto py-2 resize-y min-h-[6.5rem]`}
          placeholder={`Enter ${sectionLabel.toLowerCase()} template text…`}
        />
      </LibraryField>

      {requireCategory ? (
        <LibraryField
          id="cpt-category"
          label="Category"
          required
          error={errors.category}
          hint="Fixed categories for all Care Plan section templates."
        >
          <div className="relative">
            <select
              id="cpt-category"
              value={form.category}
              onChange={(e) => setField("category", e.target.value)}
              className={librarySelectClass(Boolean(errors.category))}
            >
              {CARE_PLAN_TEMPLATE_CATEGORIES.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
            <ChevronDownIcon
              size={14}
              className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
            />
          </div>
        </LibraryField>
      ) : null}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <LibraryField
          id="cpt-display-order"
          label="Display Order"
          error={errors.displayOrder}
          hint="Lower numbers appear first."
        >
          <input
            id="cpt-display-order"
            type="number"
            min={0}
            step={1}
            value={form.displayOrder}
            onChange={(e) => setField("displayOrder", e.target.value)}
            className={libraryInputClass(Boolean(errors.displayOrder))}
          />
        </LibraryField>

        <LibraryField id="cpt-status" label="Status">
          <div className="relative">
            <select
              id="cpt-status"
              value={form.status}
              onChange={(e) => setField("status", e.target.value)}
              className={librarySelectClass(false)}
            >
              <option value="Active">Active</option>
              <option value="Inactive">Inactive</option>
            </select>
            <ChevronDownIcon
              size={14}
              className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
            />
          </div>
        </LibraryField>
      </div>
    </LibraryFormModal>
  );
};

export default CarePlanTemplateFormModal;
